import DatePicker from 'components/DatePicker';
import React, { useEffect, useRef, useState } from 'react';
import './PageFilter.scss';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';

const MultiSelect = ({ input, options, isResetClicked, setIsResetClicked }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (isResetClicked) {
            setSelectedOptions([]);
            setIsResetClicked(false);
        }
    }, [isResetClicked, setIsResetClicked]);

    useEffect(() => {
        const handleOutsideClick = event => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, []);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const toggleOption = option => {
        if (selectedOptions.includes(option.value)) {
            setSelectedOptions(
                selectedOptions.filter(item => item !== option.value)
            );
        } else {
            setSelectedOptions([...selectedOptions, option.value]);
        }
    };

    useEffect(() => {
        input.onChange(selectedOptions);
        // eslint-disable-next-line
    }, [selectedOptions]);

    const handleCheckboxChange = option => {
        toggleOption(option);
    };

    return (
        <div className="multi-select" ref={dropdownRef}>
            <div className="selected-options" onClick={toggleDropdown}>
                {selectedOptions.length === 0 ? (
                    <span className="placeholder">Select Any</span>
                ) : (
                    // selectedOptions.join(', ')
                    `${selectedOptions.length} Selected`
                )}
            </div>
            {isOpen && (
                <div className="dropdown">
                    {options.map(option => (
                        <label
                            key={option.value}
                            className="checkbox-container"
                        >
                            <input
                                type="checkbox"
                                value={option.value}
                                checked={selectedOptions.includes(option.value)}
                                onChange={() => handleCheckboxChange(option)}
                                className="checkmark"
                            />
                            <span className="checkbox-label">
                                {option.label}
                            </span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

MultiSelect.propTypes = {
    input: PropTypes.object,
    options: PropTypes.array,
    onChangeHandler: PropTypes.func,
    isResetClicked: PropTypes.bool,
    setIsResetClicked: PropTypes.func,
};

const Input = ({ input, placeholder }) => {
    return (
        <input
            className="SearchInput"
            {...input}
            placeholder={placeholder}
            type="text"
        />
    );
};

Input.propTypes = {
    input: PropTypes.object,
    placeholder: PropTypes.string,
};

const renderDateInput = field => <DatePicker {...field}  />;

const DateRange = ({ field, isResetClicked, setIsResetClicked }) => {
    const [startDate, setStartDate] = useState(
        field?.defaultValue?.startDate || null
    );
    const [endDate, setEndDate] = useState(
        field?.defaultValue?.endDate || null
    );

    const [focusedInput, setFocusedInput] = useState(null);

    useEffect(() => {
        if (isResetClicked) {
            setStartDate(field?.defaultValue?.startDate);
            setEndDate(field?.defaultValue?.endDate);
            setFocusedInput(null);
            setIsResetClicked(false);
        }
        // eslint-disable-next-line
    }, [isResetClicked]);

    return (
        <div className="DateRangeWrap">
            <Field
                name={field.name}
                startDateId="tableFilterStartDate"
                endDateId="tableFilterEndDate"
                startDate={startDate}
                endDate={endDate}
                focusedInput={focusedInput}
                onSetStartDate={startDate => {
                    setStartDate(startDate);
                }}
                onSetEndDate={endDate => {
                    setEndDate(endDate);
                }}
                onFocusedInput={focusedInput => {
                    setFocusedInput(focusedInput);
                }}
                component={renderDateInput}
            />
        </div>
    );
};

DateRange.propTypes = {
    field: PropTypes.object,
    isResetClicked: PropTypes.func,
    setIsResetClicked: PropTypes.func,
};

const renderFields = ({ field, isResetClicked, setIsResetClicked }) => {
    switch (field.type) {
        case 'TEXT':
            return (
                <Field
                    key={field.name}
                    name={field.name}
                    type="text"
                    component={Input}
                    placeholder={field.placeholder}
                    isResetClicked={isResetClicked}
                    setIsResetClicked={setIsResetClicked}
                />
            );
        case 'DATERANGE':
            return (
                <DateRange
                    field={field}
                    isResetClicked={isResetClicked}
                    setIsResetClicked={setIsResetClicked}
                />
            );
        case 'MULTISELECT':
            return (
                <Field
                    key={field.name}
                    name={field.name}
                    component={MultiSelect}
                    options={field.options}
                    isResetClicked={isResetClicked}
                    setIsResetClicked={setIsResetClicked}
                />
            );
        default:
            return null;
    }
};

renderFields.propTypes = {
    field: PropTypes.object,
    isResetClicked: PropTypes.bool,
    setIsResetClicked: PropTypes.func,
};

const PageFilter = props => {
    const {
        configuration,
        handleSubmit,
        handleFilterSubmit,
        reset,
        onReset,
    } = props;

    const [isResetClicked, setIsResetClicked] = useState(false);

    const handleReset = () => {
        reset();
        onReset();
        setIsResetClicked(true);
    };

    return (
        <div className="PageFilterWrap">
            <span className="PageFilterTitle">Filters</span>
            <form
                onSubmit={handleSubmit(handleFilterSubmit)}
                className="PageFilter"
            >
                {configuration.map(field => {
                    return (
                        <div className="Filter">
                            <label className="filter-label">
                                {field.label || ''}
                            </label>
                            {renderFields({
                                field,
                                isResetClicked,
                                setIsResetClicked,
                            })}
                        </div>
                    );
                })}
                <div className="FilterActions">
                    <button type="submit" className="FilterSearch">
                        Search
                    </button>
                    <button
                        type="reset"
                        className="FilterReset"
                        onClick={handleReset}
                    >
                        Reset
                    </button>
                </div>
            </form>
        </div>
    );
};

PageFilter.propTypes = {
    reset: PropTypes.func,
    configuration: PropTypes.array,
    handleSubmit: PropTypes.func,
    handleFilterSubmit: PropTypes.func,
    onReset: PropTypes.func,
};

export default reduxForm({
    form: 'pageFilterForm',
    enableReinitialize: true,
})(PageFilter);
